import { Bot, Mail, Sparkles } from "lucide-react";
import React, { useState } from "react";
import Button from "./Button";

interface NewsletterSignupProps {
  variant?: "inline" | "modal" | "footer";
  title?: string;
  description?: string;
  ctaText?: string;
  source?: string; // For tracking signup sources
  botAccess?: boolean; // For exclusive bot access
  onSuccess?: (email: string) => void; // Callback for successful signup
}

const NewsletterSignup: React.FC<NewsletterSignupProps> = ({
  variant = "inline",
  title,
  description,
  ctaText,
  source = "website",
  botAccess = false,
  onSuccess,
}) => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState("");

  // Default content based on variant and botAccess
  const getDefaultTitle = () => {
    if (botAccess) return "Get Exclusive AI Bot Access";
    if (variant === "footer") return "Get Cultural Insights";
    return "Stay Updated";
  };

  const getDefaultDescription = () => {
    if (botAccess)
      return "Join 500+ property professionals getting exclusive access to our AI Culture Bot plus weekly insights.";
    if (variant === "footer")
      return "Weekly insights on transforming spaces into cultural experiences.";
    return "Get the latest cultural transformation insights delivered to your inbox.";
  };

  const getDefaultCTA = () => {
    if (botAccess) return "Get Bot Access";
    return "Subscribe";
  };

  const displayTitle = title || getDefaultTitle();
  const displayDescription = description || getDefaultDescription();
  const displayCTA = ctaText || getDefaultCTA();

  // Email validation
  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      setError("Email is required");
      return;
    }

    if (!validateEmail(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setError("");
    setIsSubmitting(true);

    try {
      // Simulate API call - replace with actual implementation
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // TODO: Replace with actual newsletter API call
      console.log("Newsletter signup:", { email, source, botAccess });

      setIsSuccess(true);
      setEmail("");

      // Call success callback if provided
      if (onSuccess) {
        onSuccess(email);
      }
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success state
  if (isSuccess) {
    return (
      <div
        className={`text-center ${
          variant === "footer" ? "text-white" : "text-gray-900"
        }`}
      >
        <div className="flex items-center justify-center mb-3">
          {botAccess ? (
            <Bot className="w-6 h-6 text-green-500" />
          ) : (
            <Mail className="w-6 h-6 text-green-500" />
          )}
        </div>
        <h3 className="font-semibold mb-2">
          {botAccess
            ? "Welcome to the AI Culture Bot!"
            : "Thanks for subscribing!"}
        </h3>
        <p className="text-sm opacity-80">
          {botAccess
            ? "Check your email for exclusive bot access instructions."
            : "You'll receive your first insight soon."}
        </p>
      </div>
    );
  }

  // Styling based on variant
  const containerClasses = {
    inline: "bg-white rounded-xl p-6 shadow-lg border border-gray-200",
    modal: "",
    footer: "",
  };

  const textColorClass = variant === "footer" ? "text-white" : "text-gray-900";
  const secondaryTextClass =
    variant === "footer" ? "text-gray-300" : "text-gray-600";

  return (
    <div className={containerClasses[variant]}>
      {/* Header with icon */}
      <div className="flex items-center justify-center mb-4">
        {botAccess ? (
          <div className="flex items-center space-x-2">
            <Sparkles className="w-5 h-5 text-purple-500" />
            <Bot className="w-5 h-5 text-blue-500" />
          </div>
        ) : (
          <Mail className="w-5 h-5 text-blue-500" />
        )}
      </div>

      {/* Title */}
      <h3
        className={`text-lg font-semibold ${textColorClass} text-center mb-2`}
      >
        {displayTitle}
      </h3>

      {/* Description */}
      <p className={`text-sm ${secondaryTextClass} text-center mb-6`}>
        {displayDescription}
      </p>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            className={`
              w-full px-4 py-3 border border-gray-300 rounded-lg 
              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
              ${
                variant === "footer"
                  ? "bg-gray-800 border-gray-600 text-white placeholder-gray-400"
                  : "bg-white text-gray-900"
              }
              disabled:opacity-50 disabled:cursor-not-allowed
            `}
            required
            disabled={isSubmitting}
          />
          {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
        </div>

        <Button
          variant="primary"
          size="md"
          type="submit"
          loading={isSubmitting}
          loadingText={botAccess ? "Getting Access..." : "Subscribing..."}
          className="w-full"
        >
          {displayCTA}
        </Button>
      </form>

      {/* Trust signals */}
      <div className={`text-center mt-4 text-xs ${secondaryTextClass}`}>
        {botAccess ? (
          <p>🔒 Exclusive access • No spam • Unsubscribe anytime</p>
        ) : (
          <p>🔒 No spam • Unsubscribe anytime • Weekly insights</p>
        )}
      </div>
    </div>
  );
};

export default NewsletterSignup;
